import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class F1065SchB2Service {
  constructor(private http: HttpClient) {}

  /**
   * Load Schedule B2 data (SuperPrompt V9)
   */
  loadScheduleB2Data(
    baseUrl: string,
    params: any,
    globalService: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const mergedParams = { ...globalService?.globalParams, ...params };

    return this.http.get<any>(url, {
      params: mergedParams,
      withCredentials: true
    });
  }

  /**
   * Load multi-row shareholder data (SuperPrompt V9)
   */
  loadShareholderData(
    baseUrl: string,
    globalParams: any,
    rowData: any
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject`;
    const params: any = {
      action_code: 'z57kmr',
      combination_key: (rowData.COMBINATION_KEY ?? '').toString(),
      schedule: 'B2',
      activity_key: globalParams.activity_key ?? '',
      is_issue_key_enabled: globalParams.is_issue_key_enabled ?? 'N',
      is_locked: globalParams.is_locked ?? 'N',
      issue_id: globalParams.issue_id ?? '',
      issue_id_for_tx_save: globalParams.issue_id_for_tx_save ?? '',
      issue_short_desc: globalParams.issue_short_desc ?? '',
      jcd_key: rowData.JCD_KEY ?? globalParams.jcd_key ?? '',
      jcd_ta_key: globalParams.jcd_ta_key ?? '',
      parent_me_string: globalParams.parent_me_string ?? '',
      scenario: rowData.SCENARIO_KEY ?? globalParams.scenario ?? '',
      scenarioCode: globalParams.scenarioCode ?? 'RAF',
      screen_key: globalParams.screen_key ?? '',
      screen_type: globalParams.screen_type ?? 'grid',
      tax_year: rowData.TAX_YEAR ?? globalParams.tax_year ?? '',
    };

    return this.http.get<any>(url, {
      params,
      withCredentials: true
    });
  }

  /**
   * Save Schedule B2 Form (SuperPrompt V9)
   * CRITICAL: Payload must match EXACT order from AngularJS
   */
  saveScheduleB2Form(
    baseUrl: string,
    formData: any[],
    globalService: any,
    filterParams: any = {},
    rowData: any = {},
    processName: string = 'FORM_SAVE_1065_SCHB2'
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=tkgaam`;

    // Build jsonSettings
    const jsonSettings = {
      tax_year: rowData.TAX_YEAR,
      scenario: rowData.SCENARIO_KEY,
      jcd_key: rowData.JCD_KEY,
      combination_key: rowData.COMBINATION_KEY
    };

    // Merge globalParams with filterParams (globalParams already has checkLockedbj, scenarioCode, is_issue_key_enabled)
    const mergedParams = { ...globalService?.globalParams, ...filterParams };

    // Build payload in EXACT order as AngularJS
    const payloadParts: string[] = [];
    
    const encode = (key: string, value: any) => {
      if (value === null || value === undefined) {
        return `${encodeURIComponent(key)}=`;
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    };
    
    // 1. FIRST: jsonSettings and jsonObj (MANDATORY ORDER!)
    payloadParts.push(encode('jsonSettings', JSON.stringify(jsonSettings)));
    payloadParts.push(encode('jsonObj', JSON.stringify(formData)));
    
    // 2. Add mergedParams in EXACT order as AngularJS (checkLockedbj, scenarioCode, is_issue_key_enabled are now in globalParams)
    // These are the parameters from GlobalService.globalParams merged with filterParams
    Object.keys(mergedParams).forEach(key => {
      if (key === 'process_name' || key === 'combination_key' || key === 'checkLockedbj') {
        return;
      }
      
      const value = mergedParams[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          payloadParts.push(encode(key, JSON.stringify(value)));
        } else {
          payloadParts.push(encode(key, value.toString()));
        }
      } else {
        payloadParts.push(encode(key, ''));
      }
    });
    
    // 3. Add checkLockedbj, process_name, combination_key
    payloadParts.push(encode('checkLockedbj', JSON.stringify([
      {
        leid: rowData.LEID,
        combination_key: rowData.COMBINATION_KEY,
        scenario_key: rowData.SCENARIO_KEY
      }
    ])));
    payloadParts.push(encode('process_name', processName));
    payloadParts.push(encode('combination_key', rowData.COMBINATION_KEY.toString()));

    const payloadString = payloadParts.join('&');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, payloadString, {
      headers,
      withCredentials: true
    });
  }
}

